import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { vacancyRequestsApi } from '../services/vacancyRequests';
import { logout } from './authSlice';

export const fetchVacancyRequests = createAsyncThunk(
    'vacancyRequests/fetchList',
    async ({ skip = 0, limit = 20, org_id, filters = {} }, { rejectWithValue }) => {
        try {
            return await vacancyRequestsApi.getList(skip, limit, org_id, filters);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const fetchVacancyRequestById = createAsyncThunk(
    'vacancyRequests/fetchById',
    async ({ id, organisationId }, { rejectWithValue }) => {
        try {
            return await vacancyRequestsApi.getById(id, organisationId);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const createVacancyRequest = createAsyncThunk(
    'vacancyRequests/create',
    async ({ organisationId, vacancyRequest }, { rejectWithValue, dispatch }) => {
        try {
            await vacancyRequestsApi.create(organisationId, vacancyRequest);
            // return await dispatch(fetchVacancyRequests({ skip: 0, limit: 20, org_id: organisationId })).unwrap();
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const updateVacancyRequest = createAsyncThunk(
    'vacancyRequests/update',
    async ({ id, organisationId, vacancyRequest, showAllRequests }, { rejectWithValue, dispatch }) => {
        try {
            await vacancyRequestsApi.update(id, organisationId, vacancyRequest);
            await dispatch(fetchVacancyRequestHistory({ id, organisationId })).unwrap();
            if (showAllRequests) {
                return await dispatch(fetchVacancyRequests({ skip: 0, limit: 20, org_id: "all" })).unwrap();
            } else {
                return await dispatch(fetchVacancyRequests({ skip: 0, limit: 20, org_id: organisationId })).unwrap();
            }
            // return await vacancyRequestsApi.getById(id, organisationId);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const updateVacancyRequestStatus = createAsyncThunk(
    'vacancyRequests/updateStatus',
    async ({ id, organisationId, status }, { rejectWithValue, dispatch }) => {
        try {
            await vacancyRequestsApi.update(id, organisationId, { status });
            return { id, status };
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const deleteVacancyRequest = createAsyncThunk(
    'vacancyRequests/delete',
    async ({ id, organisationId, showAllRequests }, { rejectWithValue, dispatch }) => {
        try {
            await vacancyRequestsApi.delete(id, organisationId);
            if (showAllRequests) {
                return await dispatch(fetchVacancyRequests({ skip: 0, limit: 20, org_id: "all" })).unwrap();
            } else {
                return await dispatch(fetchVacancyRequests({ skip: 0, limit: 20, org_id: organisationId })).unwrap();
            }
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const fetchVacancyRequestHistory = createAsyncThunk(
    'vacancyRequests/fetchHistory',
    async ({ id, organisationId }, { rejectWithValue }) => {
        try {
            return await vacancyRequestsApi.getHistory(id, organisationId);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const fetchApprovalRoute = createAsyncThunk(
    'vacancyRequests/fetchApprovalRoute',
    async (requestId, { rejectWithValue }) => {
        try {
            return await vacancyRequestsApi.getMovementsByVacancyRequest(requestId);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const updateMovementStatus = createAsyncThunk(
    'vacancyRequests/updateMovementStatus',
    async ({ movementId, status, requestId, organisationId }, { rejectWithValue, dispatch }) => {
        try {
            await vacancyRequestsApi.updateMovementStatus(movementId, status);
            await dispatch(fetchApprovalRoute(requestId)).unwrap();
            await dispatch(fetchVacancyRequestHistory({ id: requestId, organisationId })).unwrap();
            return { movementId, status, requestId };
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const fetchVacancyRequestByIdForUpdate = createAsyncThunk(
    'vacancyRequests/fetchByIdForUpdate',
    async ({ id, organisationId }, { rejectWithValue }) => {
        try {
            return await vacancyRequestsApi.getById(id, organisationId);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const fetchVacanciesByVacancyRequest = createAsyncThunk(
    'vacancyRequests/fetchVacanciesByVacancyRequest',
    async ({ id }, { rejectWithValue }) => {
        try {
            return await vacancyRequestsApi.getVacanciesByVacancyRequest(id);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

const initialState = {
    list: [],
    current: null,
    history: [],
    approvalRoute: [],
    vacancies: [],
    isLoading: false,
    error: null,
    totalPages: null, // Общее количество страниц для пагинации
    totalItems: null, // Общее количество записей
    showAllRequests: JSON.parse(localStorage.getItem('showAllRequests') || 'false') // Состояние для кнопки "Показать все заявки"
};

const vacancyRequestsSlice = createSlice({
    name: 'vacancyRequests',
    initialState,
    reducers: {
        clearCurrent: (state) => {
            state.current = null;
            state.error = null;
        },
        // Оптимистичное обновление статуса
        updateRequestStatusOptimistic: (state, action) => {
            const { id, status } = action.payload;
            const request = state.list.find(item => item.id === id);
            if (request) {
                request.status = status;
            }
        },
        // Откат оптимистичного обновления при ошибке
        revertRequestStatus: (state, action) => {
            const { id, originalStatus } = action.payload;
            const request = state.list.find(item => item.id === id);
            if (request) {
                request.status = originalStatus;
            }
        },
        // Обновление конкретной заявки в списке
        updateRequestInList: (state, action) => {
            const { id, updatedData } = action.payload;
            const requestIndex = state.list.findIndex(item => item.id === id);
            if (requestIndex !== -1) {
                state.list[requestIndex] = { ...state.list[requestIndex], ...updatedData };
            }
        },
        // Переключение состояния "Показать все заявки"
        toggleShowAllRequests: (state) => {
            state.showAllRequests = !state.showAllRequests;
        },
        // Установка состояния "Показать все заявки"
        setShowAllRequests: (state, action) => {
            state.showAllRequests = action.payload;
            // Сохраняем в localStorage
            localStorage.setItem('showAllRequests', JSON.stringify(action.payload));
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchVacancyRequests.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchVacancyRequests.fulfilled, (state, action) => {
                state.isLoading = false;
                // action.payload теперь содержит { items, total_pages, total_items }
                state.list = action.payload.items || action.payload || [];
                state.totalPages = action.payload.total_pages || null;
                state.totalItems = action.payload.total_items || null;
            })
            .addCase(fetchVacancyRequests.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            .addCase(fetchVacancyRequestById.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchVacancyRequestById.fulfilled, (state, action) => {
                state.isLoading = false;
                state.current = action.payload.data || null;
            })
            .addCase(fetchVacancyRequestById.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            .addCase(updateVacancyRequest.fulfilled, (state, action) => {
                state.current = action.payload.data || null;
            })

            .addCase(updateVacancyRequestStatus.pending, (state, action) => {
                // Оптимистичное обновление уже сделано через updateRequestStatusOptimistic
            })
            .addCase(updateVacancyRequestStatus.fulfilled, (state, action) => {
                // Статус уже обновлен оптимистично, дополнительных действий не требуется
            })
            .addCase(updateVacancyRequestStatus.rejected, (state, action) => {
                // При ошибке откатываем изменения
                // Это будет обработано в компоненте через revertRequestStatus
            })
            .addCase(fetchApprovalRoute.fulfilled, (state, action) => {
                state.approvalRoute = action.payload || [];
            })
            .addCase(fetchVacancyRequestHistory.fulfilled, (state, action) => {
                state.history = action.payload.data || [];
            })
            .addCase(fetchVacancyRequestByIdForUpdate.fulfilled, (state, action) => {
                const updatedRequest = action.payload.data;
                if (updatedRequest) {
                    const requestIndex = state.list.findIndex(item => item.id === updatedRequest.id);
                    if (requestIndex !== -1) {
                        state.list[requestIndex] = updatedRequest;
                    }
                }
            })
            .addCase(fetchVacanciesByVacancyRequest.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchVacanciesByVacancyRequest.fulfilled, (state, action) => {
                state.vacancies = action.payload || [];
            })
            .addCase(fetchVacanciesByVacancyRequest.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Обработка logout из authSlice
            .addCase(logout, (state) => {
                state.list = [];
                state.current = null;
                state.history = [];
                state.approvalRoute = [];
                state.vacancies = [];
                state.isLoading = false;
                state.error = null;
                state.showAllRequests = false;
            })
    }
});

export const {
    clearCurrent,
    updateRequestStatusOptimistic,
    revertRequestStatus,
    updateRequestInList,
    toggleShowAllRequests,
    setShowAllRequests
} = vacancyRequestsSlice.actions;
export const vacancyRequestsReducer = vacancyRequestsSlice.reducer;
